import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpXsrfTokenExtractor } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, lastValueFrom, Observable } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { ZardSonnerService } from 'src/app/shared/components/ui/sonner/sonner.service';
import { CsrfService } from './csrf.service';
import { EchoService } from './echo.service';

@Injectable({
  providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {

  private readonly _router: Router = inject(Router);
  private readonly _tokenExtractor: HttpXsrfTokenExtractor = inject(HttpXsrfTokenExtractor);
  private readonly _csrfService: CsrfService = inject(CsrfService);
  private readonly _authService: AuthService = inject(AuthService);
  private readonly _toast: ZardSonnerService = inject(ZardSonnerService);
  private readonly _echoService: EchoService = inject(EchoService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return from(this.handle(request, next)) as any;
  }

  async handle(request: HttpRequest<any>, next: HttpHandler) {
    let token = this._tokenExtractor.getToken();

    if (!token) {
      // Fetch CSRF cookie if token is not available
      await lastValueFrom(this._csrfService.setCsrfCookie());
      token = this._tokenExtractor.getToken();
    }

    if (token) {
      const headers: Record<string, string> = {
        'X-XSRF-TOKEN': token
      };

      // Include Pusher socket ID so Laravel's toOthers() can exclude the sender
      const socketId = this._echoService.socketId();
      if (socketId) {
        headers['X-Socket-Id'] = socketId;
      }

      request = request.clone({
        setHeaders: headers,
        withCredentials: true,
      });
    }

    return await lastValueFrom(next.handle(request).pipe(
      catchError(async (error: HttpErrorResponse) => {
        if (error.status === 419) {
          // CSRF token mismatch, get fresh token and retry
          await lastValueFrom(this._csrfService.setCsrfCookie());
          const newToken = this._tokenExtractor.getToken();

          if (newToken) {
            const retryRequest = request.clone({
              setHeaders: {
                'X-XSRF-TOKEN': newToken
              },
              withCredentials: true,
            });
            return await lastValueFrom(next.handle(retryRequest));
          }

          return undefined;
        } else if (error.status === 401) {
          try {
            await lastValueFrom(this._authService.logout());
            this._toast.error('Session abgelaufen', { description: 'Ihre Session ist abgelaufen. Bitte melden Sie sich erneut an.' });
            this._router.navigate(['/auth/login']);
          } catch (error) {
            this._toast.error('Fehler', { description: 'Beim Abmelden ist ein Fehler aufgetreten.' });
          }

          return null;
        }

        throw error;
      })
    ));
  }
}
